import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = 'https://api.admin.famousinitials.com';

interface ContentGenerationPayload {
  contentType: string;
  platforms: string[];
  requiresApproval: boolean;
  scheduledTime?: number;
  customText?: string;
}

interface SchedulePayload {
  postType: string;
  platforms: string[];
  scheduledTime: string;
  frequency: 'daily' | 'weekly' | 'once';
  enabled: boolean;
  contentType: string;
  autoApprove: boolean;
}

interface TemplatePayload {
  name: string;
  contentType: string;
  platforms: string[];
  content: {
    text?: string;
    hashtags?: string[];
    variables?: string[];
    imageCount?: number;
    videoSettings?: {
      duration: number;
      style: string;
    };
  };
}

interface PostHistoryParams {
  page?: number;
  limit?: number;
  platform?: string;
  status?: string;
  contentType?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface AnalyticsParams {
  timeRange: string;
  platform?: string;
  contentType?: string;
}

const getAuthHeader = async () => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  if (!token) {
    throw new Error('No authentication token available');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': token
  };
};

const handleResponse = async (response: Response) => {
  const data = await response.json();

  if (!response.ok) {
    const error = (data && data.message) || response.status;
    return Promise.reject(error);
  }

  return data;
};

export const socialMediaApi = {
  // Content Generation
  generateContent: async (payload: ContentGenerationPayload) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/generate`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  // Pending Posts
  getPendingPosts: async () => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/pending`, {
      method: 'GET',
      headers
    });
    return handleResponse(response);
  },

  approvePost: async (postId: string) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/posts/${postId}/approve`, {
      method: 'POST',
      headers
    });
    return handleResponse(response);
  },

  rejectPost: async (postId: string, reason?: string) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/posts/${postId}/reject`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason }),
    });
    return handleResponse(response);
  },

  batchApprove: async (postIds: string[]) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/posts/batch-approve`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ postIds }),
    });
    return handleResponse(response);
  },

  batchReject: async (postIds: string[], reason?: string) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/posts/batch-reject`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ postIds, reason }),
    });
    return handleResponse(response);
  },

  // Schedules
  getSchedules: async () => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/schedules`, {
      method: 'GET',
      headers
    });
    return handleResponse(response);
  },

  createSchedule: async (payload: SchedulePayload) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/schedules`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  updateSchedule: async (scheduleId: string, payload: SchedulePayload) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/schedules/${scheduleId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  deleteSchedule: async (scheduleId: string) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/schedules/${scheduleId}`, {
      method: 'DELETE',
      headers
    });
    return handleResponse(response);
  },

  // History
  getPostHistory: async (params: PostHistoryParams) => {
    const headers = await getAuthHeader();
    const queryString = new URLSearchParams(
      Object.entries(params)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString();

    const response = await fetch(`${API_BASE_URL}/social-media/history?${queryString}`, {
      method: 'GET',
      headers
    });
    return handleResponse(response);
  },

  exportHistory: async (params: PostHistoryParams) => {
    const headers = await getAuthHeader();
    const queryString = new URLSearchParams(
      Object.entries(params)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString();

    const response = await fetch(`${API_BASE_URL}/social-media/history/export?${queryString}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const error = response.statusText || response.status;
      return Promise.reject(error);
    }

    return response.blob(); // For CSV download
  },

  // Analytics
  getAnalytics: async (params: AnalyticsParams) => {
    const headers = await getAuthHeader();
    const queryString = new URLSearchParams(
      Object.entries(params)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString();

    const response = await fetch(`${API_BASE_URL}/social-media/analytics?${queryString}`, {
      method: 'GET',
      headers
    });
    return handleResponse(response);
  },

  // Templates
  getTemplates: async () => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/templates`, {
      method: 'GET',
      headers
    });
    return handleResponse(response);
  },

  createTemplate: async (payload: TemplatePayload) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/templates`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  updateTemplate: async (templateId: string, payload: TemplatePayload) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/templates/${templateId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  deleteTemplate: async (templateId: string) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/templates/${templateId}`, {
      method: 'DELETE',
      headers
    });
    return handleResponse(response);
  },

  // Platform Status
  getPlatformStatus: async () => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/platforms/status`, {
      method: 'GET',
      headers
    });
    return handleResponse(response);
  },

  testPlatform: async (platform: string) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/platforms/${platform}/test`, {
      method: 'POST',
      headers
    });
    return handleResponse(response);
  },

  // Metrics
  refreshPostMetrics: async (postId: string) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/posts/${postId}/refresh-metrics`, {
      method: 'POST',
      headers
    });
    return handleResponse(response);
  },

  // Bulk Operations
  deleteAllUnpostedContent: async () => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/social-media/delete-unposted`, {
      method: 'DELETE',
      headers
    });
    return handleResponse(response);
  },
};